/****
 * This file is an updated version of `shared/finder/hooks/useFinderTransferState`.
 * Use this instead of `useFinderTransferState` in Finder 2 environments.
 * These files look similar but are NOT the same and should NOT be combined.
 */

import { merge } from 'lodash';
import type { DeepPartial } from '@apollo/client/utilities';
import { state, useReactiveMap } from '@frameio/graphql-client';
import type { TransferJobTaskReactiveMapState } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTasks';
import { TransferJobTaskStatus } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTaskStatus';
import { hoursElapsedSinceInsertedAt } from '@frameio/shared/transfers/utils';
import {
  type TranscodeProgressFragment,
  useTranscodeProgressFragment,
} from '../gql/generated/TranscodeProgress';
import { assetHasAnyTranscode } from '../utils';

/**
 * Uses the TranscodeProgressFragment information in order to provide enough
 * data to useTransferState from the transfers package. This lets us provide
 * the original transfer state status to consumers.
 *
 * @param assetKey The target asset cache key
 * @return status The TransferJobTaskStatus provided by @frameio/shared/transfers/states
 */
export function useAssetTransferState(assetKey: string) {
  /**
   * @note: We are explicitly ignoring complete here. This whole process of guessing
   * the transfer state is pretty much based on having incomplete information.
   * Hopefully we get a more reliable source of transfer state from the backend
   * in the future, so we can avoid all the inference.
   */
  const { data: asset } = useTranscodeProgressFragment({
    from: assetKey,
  });

  const transferState = useReactiveMap(
    state.transfers.transferJobTasks,
    asset.id ?? ''
  );

  if (!asset || !isValidType(asset)) {
    return undefined;
  }

  const doesntNeedThumbnail =
    asset.__typename === 'UnsupportedAsset' ||
    asset.__typename === 'InteractiveAsset' ||
    asset.__typename === 'AudioAsset';

  // shortcut for assets that don't need thumbnails
  if (doesntNeedThumbnail) {
    return handleAssetWithoutThumbnails(asset, transferState);
  }

  return handleAssetsWithThumbnails(asset, transferState);
}

type ValidTypes =
  | Extract<TranscodeProgressFragment, { __typename?: 'AudioAsset' }>
  | Extract<TranscodeProgressFragment, { __typename?: 'DocumentAsset' }>
  | Extract<TranscodeProgressFragment, { __typename?: 'ImageAsset' }>
  | Extract<TranscodeProgressFragment, { __typename?: 'InteractiveAsset' }>
  | Extract<TranscodeProgressFragment, { __typename?: 'UnsupportedAsset' }>
  | Extract<TranscodeProgressFragment, { __typename?: 'VideoAsset' }>
  | Extract<TranscodeProgressFragment, { __typename?: 'ModelAsset' }>;

const validTypes: TranscodeProgressFragment['__typename'][] = [
  'AudioAsset',
  'DocumentAsset',
  'ImageAsset',
  'InteractiveAsset',
  'UnsupportedAsset',
  'VideoAsset',
  'ModelAsset',
];

function assetHasThumbnail(asset: TranscodeProgressFragment) {
  switch (asset.__typename) {
    case 'AudioAsset':
    case 'DocumentAsset':
    case 'ImageAsset':
    case 'VideoAsset':
    case 'ModelAsset':
      return Boolean(asset.thumbnailImageUrl?.url);
    case 'UnsupportedAsset':
    case 'InteractiveAsset':
      return false;
    default:
      return true;
  }
}

function assetIsTranscoded(asset: TranscodeProgressFragment) {
  if (
    asset.status === 'TRANSCODED' &&
    // Excluding InteractiveAsset because it was already TRANSCODED as an UnsupportedAsset
    asset.__typename !== 'InteractiveAsset'
  ) {
    return true;
  }

  if (
    asset.__typename === 'AudioAsset' ||
    asset.__typename === 'ImageAsset' ||
    asset.__typename === 'VideoAsset' ||
    asset.__typename === 'ModelAsset' ||
    asset.__typename === 'InteractiveAsset'
  ) {
    return assetHasAnyTranscode(asset);
  }

  if (
    asset.__typename === 'UnsupportedAsset' ||
    asset.__typename === 'DocumentAsset'
  ) {
    return !!asset.media?.transcodedAt;
  }

  return true;
}

export const isValidType = (
  asset: DeepPartial<TranscodeProgressFragment>
): asset is ValidTypes => validTypes.includes(asset.__typename);

const TRANSCODE_EXPIRATION_IN_HOURS = 24;

function handleAssetWithoutThumbnails(
  asset: ValidTypes,
  transferState: TransferJobTaskReactiveMapState
) {
  const assetId = asset.id;
  const isTranscoded = assetIsTranscoded(asset);

  const recentlyCreated =
    !asset.insertedAt ||
    hoursElapsedSinceInsertedAt(asset.insertedAt) <
      TRANSCODE_EXPIRATION_IN_HOURS;

  if (isTranscoded) {
    return {
      assetId,
      status: TransferJobTaskStatus.CANVIEW,
    };
  }

  if (asset.status === 'UPLOADED') {
    return {
      assetId,
      status: TransferJobTaskStatus.CANPREVIEW,
    };
  }

  // if there's no transferState, then this is a remote user or a C2C upload
  if (!transferState) {
    if ('deviceLink' in asset && asset.deviceLink) {
      return {
        assetId,
        status: TransferJobTaskStatus.DEVICE_UPLOADING,
      };
    }

    if (!asset.media) {
      return undefined;
    }

    return {
      assetId,
      status: recentlyCreated
        ? TransferJobTaskStatus.REMOTE_ORIGIN
        : TransferJobTaskStatus.FAILED,
    };
  }

  return transferState;
}

function handleAssetsWithThumbnails(
  asset: ValidTypes,
  transferState: TransferJobTaskReactiveMapState
) {
  const assetId = asset.id;
  const hasThumbnail = assetHasThumbnail(asset);
  const isTranscoded = assetIsTranscoded(asset);
  const recentlyCreated =
    !asset.insertedAt ||
    hoursElapsedSinceInsertedAt(asset.insertedAt) <
      TRANSCODE_EXPIRATION_IN_HOURS;

  // we don't control whether we get the thumbnail or the transcodes first, so
  // when we get the transcodes before the thumbs, we revert to Generating...
  if (isTranscoded && !hasThumbnail) {
    if (!recentlyCreated) {
      // however, if the asset is old enough that thumbnail generation has
      // permanently failed, treat it as viewable rather than spinning indefinitely.
      // (mirrors the recentlyCreated expiration pattern in handleAssetWithoutThumbnails)
      return { assetId, status: TransferJobTaskStatus.CANVIEW };
    }
    return merge({}, transferState, {
      assetId,
      status: TransferJobTaskStatus.TRANSFER_PARTS_COMPLETE,
    });
  }

  if (hasThumbnail && !isTranscoded) {
    return merge({}, transferState, {
      assetId,
      status: TransferJobTaskStatus.CANPREVIEW,
    });
  }

  if (hasThumbnail && isTranscoded) {
    return {
      assetId,
      status: TransferJobTaskStatus.CANVIEW,
    };
  }

  // if there's no transferState, then this is a remote user or a C2C upload
  if (!transferState) {
    if ('deviceLink' in asset && asset.deviceLink) {
      return {
        assetId,
        status: TransferJobTaskStatus.DEVICE_UPLOADING,
      };
    }

    if (asset.status === 'UPLOADED') {
      return {
        assetId,
        status: hasThumbnail
          ? TransferJobTaskStatus.CANPREVIEW
          : TransferJobTaskStatus.TRANSFER_PARTS_COMPLETE,
      };
    }

    if (!asset.media && !('thumbnailImageUrl' in asset)) {
      return undefined;
    }

    return {
      assetId,
      status: recentlyCreated
        ? TransferJobTaskStatus.REMOTE_ORIGIN
        : TransferJobTaskStatus.FAILED,
    };
  }

  return transferState;
}
